import { Bookmark } from '../types';
import { authService } from './auth';

interface GoogleDriveFile {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string;
}

interface GoogleDriveApiResponse {
  files: GoogleDriveFile[];
}

class GoogleDriveService {
  private readonly FOLDER_NAME = 'PatternLab Bookmarks';
  private readonly FILE_NAME = 'bookmarks.json';
  private folderId: string | null = null;
  private fileId: string | null = null;
  private folderCreationPromise: Promise<string> | null = null;
  private fileCreationPromise: Promise<string> | null = null;

  private async getAccessToken(): Promise<string> {
    // First try to get a valid token from the auth service
    const validToken = await authService.getValidAccessToken();
    if (validToken) {
      return validToken;
    }
    
    // If no valid token, throw an error
    throw new Error('No valid Google access token available. Please sign in again.');
  }

  private async makeRequest(url: string, options: RequestInit = {}): Promise<any> {
    const token = await this.getAccessToken();
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      let errorText = '';
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = 'Unable to read error response';
      }
      
      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        // Clear the expired token
        localStorage.removeItem('google_access_token');
        throw new Error('Google access token has expired. Please sign in again to continue using Google Drive features.');
      }
      
      throw new Error(`Google Drive API error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    // For DELETE requests, there's no JSON response
    if (options.method === 'DELETE') {
      return null;
    }

    // For other requests, try to parse JSON
    try {
      return await response.json();
    } catch (error) {
      try {
        return await response.text();
      } catch (e) {
        return null;
      }
    }
  }

  private async findOrCreateFolder(): Promise<string> {
    if (this.folderId) {
      return this.folderId;
    }

    // If folder creation is already in progress, wait for it
    if (this.folderCreationPromise) {
      return this.folderCreationPromise;
    }

    // Start folder creation process
    this.folderCreationPromise = this.createFolderIfNeeded();
    try {
      const folderId = await this.folderCreationPromise;
      this.folderId = folderId;
      return folderId;
    } finally {
      this.folderCreationPromise = null;
    }
  }

  private async createFolderIfNeeded(): Promise<string> {
    // Search for existing folder
    const searchUrl = `https://www.googleapis.com/drive/v3/files?q=name='${this.FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`;
    const searchResponse = await this.makeRequest(searchUrl);
    
    if (searchResponse.files && searchResponse.files.length > 0) {
      return searchResponse.files[0].id;
    }

    // Create new folder
    const createFolderUrl = 'https://www.googleapis.com/drive/v3/files';
    const folderMetadata = {
      name: this.FOLDER_NAME,
      mimeType: 'application/vnd.google-apps.folder',
    };

    const createResponse = await this.makeRequest(createFolderUrl, {
      method: 'POST',
      body: JSON.stringify(folderMetadata),
    });

    return createResponse.id;
  }

  private async findOrCreateFile(): Promise<string> {
    if (this.fileId) {
      return this.fileId;
    }

    // If file creation is already in progress, wait for it
    if (this.fileCreationPromise) {
      return this.fileCreationPromise;
    }

    // Start file creation process
    this.fileCreationPromise = this.createFileIfNeeded();
    try {
      const fileId = await this.fileCreationPromise;
      this.fileId = fileId;
      return fileId;
    } finally {
      this.fileCreationPromise = null;
    }
  }

  private async createFileIfNeeded(): Promise<string> {
    const folderId = await this.findOrCreateFolder();

    // Search for existing file
    const searchUrl = `https://www.googleapis.com/drive/v3/files?q=name='${this.FILE_NAME}' and '${folderId}' in parents and trashed=false`;
    const searchResponse = await this.makeRequest(searchUrl);
    
    if (searchResponse.files && searchResponse.files.length > 0) {
      return searchResponse.files[0].id;
    }

    // Create new file
    const createFileUrl = 'https://www.googleapis.com/drive/v3/files';
    const fileMetadata = {
      name: this.FILE_NAME,
      parents: [folderId],
      mimeType: 'application/json',
    };

    const createResponse = await this.makeRequest(createFileUrl, {
      method: 'POST',
      body: JSON.stringify(fileMetadata),
    });

    return createResponse.id;
  }

  async saveBookmarks(bookmarks: Bookmark[]): Promise<void> {
    try {
      const fileId = await this.findOrCreateFile();
      const content = JSON.stringify(bookmarks, null, 2);
      
      // Upload content
      const uploadUrl = `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`;
      await this.makeRequest(uploadUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: content,
      });
    } catch (error) {
      console.error('Failed to save bookmarks to Google Drive:', error);
      throw error;
    }
  }

  async loadBookmarks(): Promise<Bookmark[]> {
    try {
      const fileId = await this.findOrCreateFile();
      
      // Download content
      const downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;
      const response = await this.makeRequest(downloadUrl);
      
      if (response && Array.isArray(response)) {
        return response;
      }
      
      return [];
    } catch (error) {
      // Don't log the error if it's just that the file doesn't exist yet
      if (error instanceof Error && error.message.includes('404')) {
        return [];
      }
      console.error('Failed to load bookmarks from Google Drive:', error);
      return [];
    }
  }

  async deleteBookmarks(): Promise<void> {
    try {
      if (this.fileId) {
        const deleteUrl = `https://www.googleapis.com/drive/v3/files/${this.fileId}`;
        await this.makeRequest(deleteUrl, {
          method: 'DELETE',
        });
        
        this.fileId = null;
      }
    } catch (error) {
      console.error('Failed to delete bookmarks from Google Drive:', error);
      throw error;
    }
  }

  // Check if user has granted Drive permissions
  async checkDrivePermissions(): Promise<boolean> {
    try {
      await this.makeRequest('https://www.googleapis.com/drive/v3/about?fields=user');
      return true;
    } catch (error) {
      // If it's a 401 error, the token has expired
      if (error instanceof Error && error.message.includes('expired')) {
        console.warn('Google access token has expired. Please sign in again to use Google Drive features.');
        return false;
      }
      // For other errors, just return false
      console.warn('Google Drive permissions check failed:', error);
      return false;
    }
  }
}

export const googleDriveService = new GoogleDriveService();
